const BIRR = new Intl.NumberFormat('en-ET', {
  style: 'currency',
  currency: 'ETB',
  maximumFractionDigits: 2,
})

export function birr(amount: string | number): string {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  return Number.isFinite(value) ? BIRR.format(value) : '—'
}

/** Both prices come from the API; exchange-rate configuration stays server-side. */
export function planPrice(plan: { monthly_etb: string | number; monthly_usd?: string | null }): string {
  const etb = birr(plan.monthly_etb)
  if (plan.monthly_usd == null) return etb
  const usd = Number(plan.monthly_usd)
  return Number.isFinite(usd) ? `${etb} · USD ${usd.toFixed(2)}` : etb
}

const BIRR_WHOLE = new Intl.NumberFormat('en-ET', {
  style: 'currency',
  currency: 'ETB',
  maximumFractionDigits: 0,
})

/** For headline tiles: the cents are noise at a glance and cost the width
 *  that makes a five-figure total overflow its tile. */
export function birrWhole(amount: string | number): string {
  const value = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  return Number.isFinite(value) ? BIRR_WHOLE.format(value) : '—'
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'

  const seconds = Math.round((Date.now() - then) / 1000)
  if (seconds < 45) return 'just now'

  const units: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, 'second'],
    [3600, 'minute'],
    [86400, 'hour'],
    [604800, 'day'],
    [2629800, 'week'],
    [31557600, 'month'],
    [Infinity, 'year'],
  ]

  const formatter = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  let divisor = 1
  for (const [limit, unit] of units) {
    if (Math.abs(seconds) < limit) {
      return formatter.format(-Math.round(seconds / divisor), unit)
    }
    divisor = limit
  }
  return '—'
}

/** Whole days from now until `iso`, rounded up; negative once it has passed.
 *  `relativeTime` cannot answer this — it reports every future moment as
 *  "just now", because it was written to describe things that have happened. */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return null
  return Math.ceil((then - Date.now()) / 86_400_000)
}

const CALENDAR = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
const CALENDAR_WITH_YEAR = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

/** A renewal date. The year is noise until the date is in another one. */
export function calendarDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const formatter =
    date.getFullYear() === new Date().getFullYear() ? CALENDAR : CALENDAR_WITH_YEAR
  return formatter.format(date)
}

export function duration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${Math.round(seconds)}s`
  const minutes = Math.floor(seconds / 60)
  return `${minutes}m ${Math.round(seconds % 60)}s`
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7)
}

const TRIGGER_TITLE: Record<string, string> = {
  push: 'Pushed commit',
  manual: 'Manual deploy',
  rollback: 'Rollback',
  redeploy: 'Redeploy',
}

/**
 * What to call a deploy in a list.
 *
 * A deploy started from the dashboard has no commit message to show — the
 * commit was never fetched — and "no commit message" reads like something went
 * wrong. Naming what actually happened is both shorter and true.
 */
export function deployTitle(deployment: {
  commit_message: string | null
  trigger: string
}): string {
  const line = (deployment.commit_message ?? '').split('\n')[0].trim()
  return line || TRIGGER_TITLE[deployment.trigger] || 'Deploy'
}

export function money(value: string | number, currency = 'ETB'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2 }).format(Number(value))
}
